import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Divider from '@material-ui/core/Divider'
import Hidden from '@material-ui/core/Hidden'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import { withStyles } from '@material-ui/core/styles'
import logo from '../../logo.png'

const styles = (theme) => ({
  drawer: {
    width: theme.custom.sidebarWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: theme.custom.sidebarWidth,
    background: 'linear-gradient(180deg, #f7f8fc 0%, #f1f3f9 100%)',
    borderRight: 'none',
    padding: theme.spacing(2, 1.5),
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 1.5, 2),
  },
  brandText: {
    fontWeight: 700,
    fontSize: '0.95rem',
    lineHeight: 1.25,
  },
  logo: {
    width: 36,
    height: 36,
    borderRadius: 10,
  },
  sectionLabel: {
    padding: theme.spacing(2, 2, 0.75),
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  item: {
    borderRadius: 12,
    marginBottom: 4,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(91, 108, 255, 0.08)',
    },
  },
  itemActive: {
    backgroundColor: 'rgba(91, 108, 255, 0.14) !important',
    color: theme.palette.primary.dark,
    '& $icon': {
      color: theme.palette.primary.main,
    },
  },
  icon: {
    minWidth: 40,
    color: theme.palette.text.secondary,
  },
  secondaryWrap: {
    marginTop: theme.spacing(1),
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
  },
})

function renderItems(items, classes, onNavigate) {
  let lastSection = null
  const nodes = []

  items.forEach((item) => {
    if (item.section && item.section !== lastSection) {
      lastSection = item.section
      nodes.push(
        <Typography key={`section-${item.section}`} className={classes.sectionLabel}>
          {item.section}
        </Typography>,
      )
    }

    const content = (
      <ListItem
        button
        selected={!!item.active}
        className={`${classes.item}${item.active ? ` ${classes.itemActive}` : ''}`}
        onClick={(e) => {
          if (item.onClick) {
            e.preventDefault()
            item.onClick()
          }
          if (onNavigate) onNavigate()
        }}
      >
        <ListItemIcon className={classes.icon}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ style: { fontWeight: item.active ? 700 : 500, fontSize: '0.92rem' } }}
        />
      </ListItem>
    )

    if (item.to && !item.onClick) {
      nodes.push(
        <Link key={item.key} to={item.to} style={{ textDecoration: 'none', color: 'inherit' }}>
          {content}
        </Link>,
      )
    } else {
      nodes.push(<React.Fragment key={item.key}>{content}</React.Fragment>)
    }
  })

  return nodes
}

function Sidebar({ classes, open, onClose, navItems = [], secondaryNav = [] }) {
  const content = (
    <div>
      <Hidden mdUp>
        <div className={classes.toolbar}>
          <IconButton onClick={onClose}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
      </Hidden>
      <Link to="/groups" style={{ textDecoration: 'none', color: 'inherit' }} onClick={onClose}>
        <div className={classes.brand}>
          <img src={logo} alt="logo" className={classes.logo} />
          <Typography className={classes.brandText}>
            GOLAB Gerontech Platform
          </Typography>
        </div>
      </Link>
      <List disablePadding>{renderItems(navItems, classes, onClose)}</List>
      {secondaryNav.length > 0 ? (
        <div className={classes.secondaryWrap}>
          <Divider style={{ margin: '12px 8px' }} />
          <Typography className={classes.sectionLabel}>指標</Typography>
          <List disablePadding>{renderItems(secondaryNav, classes, onClose)}</List>
        </div>
      ) : null}
    </div>
  )

  return (
    <React.Fragment>
      <Hidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{ keepMounted: true }}
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          className={classes.drawer}
          variant="permanent"
          open
          classes={{ paper: classes.drawerPaper }}
        >
          {content}
        </Drawer>
      </Hidden>
    </React.Fragment>
  )
}

Sidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  navItems: PropTypes.array,
  secondaryNav: PropTypes.array,
}

export default withStyles(styles)(Sidebar)
